import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Coaching, GpsOnly, VideoSection, Workout } from '@kinomap-dev/ng-ui-lib/core';
import { ApiService } from './api.service';
import { HttpResourceRef } from '@angular/common/http';

export type GlobalContentDataResponse = {
  scenics: VideoSection[];
  coachings: Coaching[];
  workouts: Workout[];
  gpsOnly: GpsOnly[];
};

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private readonly apiService: ApiService = inject(ApiService);

  readonly term: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
  private debounceTimer: ReturnType<typeof setTimeout> | undefined;

  private readonly responseCount: number = 3;

  private readonly scenicsRessources: HttpResourceRef<VideoSection[] | undefined> =
    this.apiService.getScenicContentRessources(1, this.responseCount, 'asc', this.term);
  private readonly coachingsRessources: HttpResourceRef<Coaching[] | undefined> =
    this.apiService.getCoachingContentsRessources(1, this.responseCount, 'asc', this.term);
  private readonly workoutsRessources: HttpResourceRef<Workout[] | undefined> =
    this.apiService.getWorkoutContentsRessources(1, this.responseCount, 'asc', this.term);
  private readonly gpsOnlysRessources: HttpResourceRef<GpsOnly[] | undefined> =
    this.apiService.getGpsOnlyContentsRessources(1, this.responseCount, 'asc', this.term);

  readonly isLoading: Signal<boolean> = computed(
    () =>
      this.scenicsRessources.isLoading() ||
      this.coachingsRessources.isLoading() ||
      this.workoutsRessources.isLoading() ||
      this.gpsOnlysRessources.isLoading(),
  );

  readonly globalData: Signal<GlobalContentDataResponse | undefined> = computed<
    GlobalContentDataResponse | undefined
  >(() => {
    const scenics: VideoSection[] | undefined = this.scenicsRessources.value();
    const coachings: Coaching[] | undefined = this.coachingsRessources.value();
    const workouts: Workout[] | undefined = this.workoutsRessources.value();
    const gpsOnlys: GpsOnly[] | undefined = this.gpsOnlysRessources.value();

    if (!scenics || !coachings || !workouts || !gpsOnlys) return undefined;
    return { scenics, coachings, workouts, gpsOnly: gpsOnlys };
  });

  globalContentResearch(term: string): void {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.term.set(term), 400);
  }
}
